/**
 * The phrases of a form.
 *
 * @typedef {Object} FormPhrases
 * @property {string} looking_up_contact
 * @property {string} contact_found
 * @property {string} contact_not_found
 * @property {string} button_clear_lookup_field
 * @property {string} button_trigger_lookup
 * @property {string} error_during_lookup
 * @property {string} contact_currently_set
 */

/**
 * The field binding definition.
 *
 * @typedef {Object} FormFieldData
 * @property {string|string[]} upbeat_property
 * @property {string} field_selector
 * @property {string} field_container_selector
 * @property {boolean} sensitive
 * @property {HTMLElement} _$field Runtime generate reference to the HTML field.
 * @property {HTMLElement} _$container Runtime generate reference to the HTML container.
 */

/**
 * The form data definition.
 *
 * @typedef {Object} FormData
 * @property {string} form_selector
 * @property {string} lookup_property
 * @property {string} lookup_url
 * @property {FormFieldData[]} fields
 * @property {FormPhrases} phrases
 * @property {boolean} manually_trigger_lookup
 * @property {string} instance_id
 * @property {boolean} lock_property_fields
 * @property {boolean} lock_property_fields_if_empty
 */

import {dispatchEvent} from "./lib/events";
import {isVisible} from "./lib/dom";

const isTribe = document.body.classList.contains('page-tribe-attendee-registration');
const title_property = 'title_as_option';
const title_other_property = 'title';

/**
 * Try to resolve to a field given a property name.
 *
 * @param {string} propertyName
 * @param {FormFieldData[]} fields
 * @Param {string} candidateFirstOrLast Can be useful if more than on field is mapped to the same property (e.g. an other field like Title Other mapping to title also).
 *
 * @return null|FormFieldData
 */
function resolveLookupField(propertyName, fields, candidateFirstOrLast = 'first' ) {
    const candidates = fields.filter(field => field.upbeat_property === propertyName);
    if (candidates.length === 0) {
        return null;
    }

    return candidateFirstOrLast === 'last' ? candidates[candidates.length - 1] : candidates[0];
}
window.resolveLookupField = resolveLookupField;

/**
 * Calls the provided callback when the throttle duration expires. NB: CANCELS AND
 * STOPS PROPAGATION OF THE ORIGINAL EVENT!
 *
 * @param {EventTarget} $element The element to attach listener to.
 * @param {string} event The event to attach.
 * @param {function} callback The event callback.
 * @param {int} throttleDuration The throttle period in milliseconds.
 * @return void
 */
function throttledEvent($element, event, callback, throttleDuration = 500) {
    const timeoutKey = `_lookupTimeout_${event}`;

    $element[timeoutKey] = null;

    $element.addEventListener(event, originalEvent => {
        originalEvent.stopImmediatePropagation();
        originalEvent.preventDefault();

        if ($element[timeoutKey] !== null) {
            window.clearTimeout($element[timeoutKey]);
            $element[timeoutKey] = null;
        }

        $element[timeoutKey] = window.setTimeout(() => {
            callback({originalEvent});
        }, throttleDuration);
    });
}

/**
 * If there was a suitable response, update the container fields accordingly.
 *
 * @param {HTMLElement} $container The container to find fields in.
 * @param {HTMLElement} $lookupInitiator The field which initiated the lookup.
 * @param {FormData} formData The form config data.
 * @param {mixed} data The incoming response from the server.
 *
 * @return {boolean} If a contact was found and form updated, true. Otherwise false.
 */
function handleResponseIntoForm($container, $lookupInitiator, formData, data) {
    if (!('success' in data)) {
        console.error('Unexpected response format.', data);
        return false;
    }

    if (!data.success) {
        console.error(`Error with response. ${data.code || '(NO CODE)'}: ${data.error || 'No error detail provided.'}`)
        return false;
    }

    if (!('contact' in data)) {
        console.error('Incomplete response data.', data);
        return false;
    }

    const contact = data.contact;

    formData.fields.forEach(field => {
        const $field = $container.querySelector(field.field_selector);

        // Could not locate field, or it is not visible.
        if (!$field || !isVisible($field)) {
            return;
        }

        if ($field === $lookupInitiator) {
            return;
        }

        // Ensure the singular or compound properties actually exist in the contact response.
        const properties = (Array.isArray(field.upbeat_property) ? field.upbeat_property : [field.upbeat_property])
            .filter(property => property in contact);

        if (!properties.length) {
            console.log('This property could not be resolved from the contact response.', field.upbeat_property);
            return;
        }

        $field.value = properties.map(property => contact[property])
            .filter(value => !!value)
            .join(' ');

        // Add any important classes to the field.
        (field._$container || field._$field).classList.add('agend-upbeat-contact-lookup__property-populated');

        setFieldSensitivityState(field);

        // Dispatch a change event to trigger the field validation.
        $field.dispatchEvent(new Event('change', {bubbles: true}));
    });

    return true;
}

/**
 * Updates the html element classes based on the state of the field and the data, *if* the field
 * is configured as a sensitive field.
 * @param {FormFieldData} field The field to update.
 * @param {boolean} hasValue If the field has a value or not. This will determine application of sensitivity classes.
 */
function setFieldSensitivityState(field, hasValue = true) {
    if (!field.sensitive) {
        return;
    }

    const $target = field._$container || field._$field;
    const className = 'agend-upbeat-contact-lookup__sensitive-property';

    if (hasValue) {
        $target.classList.add(className);
    } else {
        $target.classList.remove(className);
    }
}

/**
 * Attempt to set sensitivity state for all fields in the form definition.
 * @param {FormData} formData The form definition.
 * @param {boolean} hasValue If the field has a value or not. This will determine application of sensitivity classes.
 */
function setAllFieldSensitivityStates(formData, hasValue = true) {
    formData.fields.forEach(field => setFieldSensitivityState(field, hasValue));
}


/**
 * Empty all the relevant fields in the container.
 *
 * @param {HTMLElement} $container The container to find fields in.
 * @param {HTMLElement} $lookupField The field which initiated the lookup.
 * @param {FormData} formData The form config data.
 *
 * @return {boolean} If a contact was found and form updated, true. Otherwise false.
 */
function emptyFormFields($container, $lookupField, formData) {
    formData.fields.forEach(field => {
        const $field = $container.querySelector(field.field_selector);

        // Could not locate field, or it is not visible.
        if (!$field || !isVisible($field)) {

            // Clear the Other field hidden with JS.
            if (field.upbeat_property === title_property) {
                $field.value = '';
            }

            // We still need to account for sensitive fields (such as phone for eg). If we don't, when someone
            // clears the form, or that field is otherwise made visible it will still retain the provided
            // potentially sensitive value.
            if (!field.sensitive) {
                return;
            }
        }

        if ($field === $lookupField) {
            return;
        }

        $field.value = '';

        // Clear out any important class names from the container (or field if no container).
        [
            'property-populated',
            'sensitive-property',
        ].forEach(className => (field._$container || field._$field).classList.remove(`agend-upbeat-contact-lookup__${className}`));

        setFieldSensitivityState(field, false);

        // Dispatch a change event to trigger the field validation.
        $field.dispatchEvent(new Event('change', {bubbles: true}));
    });

    dispatchEvent($container, 'agend.contact-lookup.form-fields-emptied', {formData, $lookupField});
}

/**
 * Creates an invisible and unusable container with all the phrases. This gives things like
 * FontAwesome a chance to pre-load assets/glyphs/etc before the phrases are actually used in
 * form, preventing "pop-in"/etc.
 *
 * @param {FormPhrases} phrases The phrases object to prepare.
 */
function preparePhrasesInvisibly(phrases) {
    const $container = document.createElement('div');
    $container.style.width = '0px';
    $container.style.height = '0px';
    $container.style.position = 'absolute';
    $container.style.top = '-1000px';
    $container.style.overflow = 'hidden'
    $container.setAttribute('tabindex', '-1');
    $container.setAttribute('aria-hidden', 'true');

    Object.keys(phrases).forEach(key => {
        const $phrase = document.createElement('span');
        $phrase.innerHTML = phrases[key];
        $container.appendChild($phrase);
    });

    document.querySelector('body').append($container);
}

/**
 * Locks a field, preventing it from being interacted with.
 *
 * @param $field
 * @param $form
 * @param formData
 * @param $lookupField
 * @param $propertyAndLookupFields
 */
function lockField($field, $form, formData, $lookupField, $propertyAndLookupFields) {

    // If there is already a tab index, capture that into a data attribute.
    if ($field.hasAttribute('tabindex')) {
        $field.dataset.agendContactLookupOriginalTabIndex = $field.getAttribute('tabindex');
    }

    // Really try make our fields readonly (this is paired with pointer/touch events in CSS). Mostly this
    // is to make <select> fields actually readonly... Why can they still be interacted with :sad-face:
    [
        ['readonly', 'readonly'],
        ['tabindex', '-1'],
    ].forEach(([attribute, value]) => $field.setAttribute(attribute, value));

    // Update the field container, if there was one.
    if ($field?._agendUpbeatContactLookupField?._$container) {
        $field._agendUpbeatContactLookupField._$container.classList.add('agend-upbeat-contact-lookup__property-container--locked');

        dispatchEvent($form, 'agend.contact-lookup.property-container-locked', {
            formData,
            $lookupField,
            $propertyAndLookupFields
        });
    }
}

/**
 * Unlocks a field, allowing it to be interacted with.
 *
 * @param $field
 * @param formData
 */
function unlockField($field, formData) {
    ['readonly', 'tabindex'].forEach(attribute => $field.removeAttribute(attribute));

    // If there was a tab index, restore it.
    if ($field.dataset.agendContactLookupOriginalTabIndex) {
        $field.setAttribute('tabindex', $field.dataset.agendContactLookupOriginalTabIndex);
        delete $field.dataset.agendContactLookupOriginalTabIndex;
    }

    // Update the field container, if there was one.
    if ($field?._agendUpbeatContactLookupField?._$container) {
        $field._agendUpbeatContactLookupField._$container.classList.remove('agend-upbeat-contact-lookup__property-container--locked');

        dispatchEvent($field, 'agend.contact-lookup.property-container-unlocked', { formData });
    }
}


/**
 * Is the "For the first attendee on an event registration, lock the attendee to the currently logged in user's details?"
 * setting enabled in Agend settings > Event?
 *
 * @returns {boolean}
 */
function isFirstAttendeeReadonlySettingEnabled() {
    return document.body.classList.contains('agend-pi-event-registration-first-attendee-readonly');
}

/**
 * Prepare a form for lookup behaviour. Modifies inputs, binds events, injects elements.
 * @todo This has gotten a little heavy, could do with some cleanup at some point.
 *
 * @param {FormData} formData The form config data.
 */
function prepareFormForLookup(formData) {
    if (!formData.lookup_property) {
        console.warn('Form data provided with no lookup field.', formData);
        return;
    }

    const lookupField = resolveLookupField(formData.lookup_property, formData.fields);

    if (!lookupField) {
        console.warn(`Could not locate a lookup field related to "${formData.lookup_property}"...`, formData);
        return;
    }

    const $form = document.querySelector(formData.form_selector);

    if (!$form) {
        console.warn(`Unable to locate form with selector "${formData.form_selector}"`);
        return;
    }

    preparePhrasesInvisibly(formData.phrases);

    // Build a key to check for duplicate bindings. By using an instance ID like this, we can
    // create multiple instances of lookups on the same form without conflicting with each other.
    // The best example of this is the Tribe Attendee form, where it is a single form containing
    // multiple sets of Attendee fields.
    const datasetKey = `upbeat-form-${formData.instance_id}`;
    if ($form.dataset[datasetKey]) {
        console.info('Looks like this container has already been setup for this formData instance.', formData);
        return;
    }

    const $lookupField = $form.querySelector(lookupField.field_selector);

    if (!$lookupField) {
        console.warn(`Unable to locate lookup field with selector "${lookupField.field_selector}"`);
        return;
    }

    if (isFirstAttendeeReadonlySettingEnabled()) {
        const firstAttendeeContainer = document.querySelector('.tribe-tickets__attendee-tickets-item:first-of-type');
        if (firstAttendeeContainer?.contains($lookupField)) {
            console.info('The Agend setting to set the first attendee fields to be readonly is enabled, skipping contact lookup setup for the first attendee.');
            return;
        }
    }

    formData._$lookupField = $lookupField;

    const $lookupFieldContainer = $lookupField.closest(lookupField.field_container_selector);

    if ($lookupFieldContainer) {
        $lookupFieldContainer.classList.add('agend-upbeat-contact-lookup');
    }

    $lookupField.classList.add('agend-upbeat-contact-lookup__field');

    // Locate the respective fields/containers, and set in the data object. Also sets a class on the container
    // if there was one found.
    formData.fields.forEach(field => {
        field._$field = $form.querySelector(field.field_selector);
        if (field._$field) {
            field._$container = field._$field.closest(field.field_container_selector);

            if (field._$container) {
                field._$container.classList.add('agend-upbeat-contact-lookup__property-container');
            }

            // Add a handy self reference from the element.
            field._$field._agendUpbeatContactLookupField = field;
        }
    });

    const $targetPropertyFields = formData.fields
        // Don't use the lookup field.
        .filter(field => field.upbeat_property !== lookupField.upbeat_property)
        // ...locate the actual elements.
        .map(field => field._$field)
        // ...filter missing entries.
        .filter($field => !!$field);

    $targetPropertyFields.forEach($field => $field.classList.add('agend-upbeat-contact-lookup__property-target'));

    // Create a copy of property and lookup fields, mostly for easy locking.
    const $propertyAndLookupFields = [...$targetPropertyFields];
    $propertyAndLookupFields.push($lookupField);

    const appendElement = ($element) => {
        // Add it to the container.
        if ($lookupFieldContainer) {
            $lookupFieldContainer.append($element);
        } else {
            // ...otherwise put it after the input directly.
            $lookupField.after($element);
        }
    };

    /**
     * Lock the target property fields.
     *
     * Note: If you don't want to lock fields when they don't have a value, add this filter into your client plugin:
     * add_filter( 'agend_contact_lookup_form_binding_lock_property_fields_if_empty', '__return_false' );
     */
    const maybeLockFields = () => {
        if (!formData.lock_property_fields) {
            return;
        }

        $propertyAndLookupFields
            .forEach($field => lockField($field, $form, formData, $lookupField, $propertyAndLookupFields));
    };

    const maybeUnlockFields = (includeLookupField = true) => {
        if (!formData.lock_property_fields) {
            return;
        }

        // Unlock the target property fields.
        $propertyAndLookupFields
            .forEach($field => {
                if (!includeLookupField && $field === $lookupField) {
                    return;
                }
                unlockField($field, formData);
            });
    }

    const setMessage = (message, data = {}) => {
        if (('contact' in data)) {
            // Replace any tokens in message with the values from data.
            message = message.replace(/\{([^}]+)\}/g, (_, token) => data.contact[token]);
        }

        $messageContainer.innerHTML = message;
        ($lookupFieldContainer || $lookupField).classList.add('agend-upbeat-contact-lookup--displaying-message');

        // Ensure sizing matches the message being displayed.
        resolveSizes();
    };

    const clearMessage = () => {
        $messageContainer.innerHTML = '';
        ($lookupFieldContainer || $lookupField).classList.remove('agend-upbeat-contact-lookup--displaying-message');

        // Ensure sizing matches the message being displayed.
        resolveSizes();
    };

    const $messageContainer = document.createElement('p');
    $messageContainer.classList.add('agend-upbeat-contact-lookup__message');

    // Detect and apply the input's height, just in case there is some unusual alignment
    // or spacing (eg, Agend Theme injects wrappers and very specific padding/margins).
    // We also account for the size of the "Clear" button here too.
    const resolveSizes = () => {
        const actionsHeight = resolveFieldHeight($lookupFieldContainer, $lookupField);
        if (actionsHeight && $lookupFieldContainer) {
            $lookupFieldContainer.style.setProperty('--actions-height', `${actionsHeight}px`);
        }

        // Determine the size of the clear button, and if necessary set the CSS var value to match.
        const clearButtonWidth = $lookupFieldContainer.querySelector('[data-action="clear"]')?.getBoundingClientRect()?.width;
        if (clearButtonWidth && $lookupFieldContainer) {
            $lookupFieldContainer.style.setProperty('--input-button-overlap', `${clearButtonWidth}px`);
        }

        const messageHeight = $messageContainer?.getBoundingClientRect()?.height;
        if ($messageContainer && messageHeight) {
            $lookupFieldContainer.style.setProperty('--message-height', `${messageHeight}px`);
        }
    };

    const performLookup = () => {
        return new Promise((resolve, reject) => {
            if (!$lookupField.value.trim().length) {
                setMessage(formData.phrases.no_search_value);
                const reason = 'No value to use for search.';
                reject({success: false, code: 400, error: reason});
                dispatchEvent($form, 'agend.contact-lookup.error', {formData, error: reason, $lookupField});
                setAllFieldSensitivityStates(formData, false);
                maybeUnlockFields();
                return;
            }

            setMessage(formData.phrases.looking_up_contact);

            emptyFormFields($form, $lookupField, formData);

            const url = formData.lookup_url
                .replace('{lookup_property}', encodeURIComponent(formData.lookup_property))
                .replace('{lookup_value}', encodeURIComponent($lookupField.value));

            fetch(url)
                // Build JSON...
                .then(response => response.json())
                // ...then deal with it
                .then(data => {
                    dispatchEvent($form, 'agend.contact-lookup.lookup-response', {formData, data, $lookupField});
                    if (handleResponseIntoForm($form, $lookupField, formData, data)) {
                        setMessage(formData.phrases.contact_found, data);
                        dispatchEvent($form, 'agend.contact-lookup.contact-found', {formData, data, $lookupField});
                    } else {
                        setMessage(formData.phrases.contact_not_found, data);
                        setAllFieldSensitivityStates(formData, false);
                        maybeUnlockFields(false);
                        dispatchEvent($form, 'agend.contact-lookup.contact-not-found', {formData, data, $lookupField});
                    }
                    resolve(data);
                })
                .catch(response => {
                    console.error('There was an error looking up the contact.', response);
                    setMessage(formData.phrases.error_during_lookup);
                    dispatchEvent($form, 'agend.contact-lookup.error', {formData, response, $lookupField});
                    reject(response);
                    setAllFieldSensitivityStates(formData, false);
                    maybeUnlockFields();
                })
            ;
        });

    };

    // If we want to trigger lookup manually, create the buttons.
    if (formData.manually_trigger_lookup) {
        const $actionContainer = document.createElement('p');
        $actionContainer.classList.add('agend-upbeat-contact-lookup__actions')
        $actionContainer.innerHTML = `
            <button data-action="clear" class="agend-upbeat-contact-lookup__action agend-upbeat-contact-lookup__action--clear">${formData.phrases.button_clear_lookup_field}</button>
            <button data-action="lookup" class="agend-upbeat-contact-lookup__action agend-upbeat-contact-lookup__action--search">${formData.phrases.button_trigger_lookup}</button>
        `;

        const $actionButtons = $actionContainer.querySelectorAll('button');

        throttledEvent(
            $actionContainer,
            'click',
            (data) => {
                const target = data.originalEvent.target;

                if (target.tagName !== 'BUTTON') {
                    return;
                }

                // Disable the buttons...
                $actionButtons.forEach($button => $button.setAttribute('disabled', 'disabled'));

                // Clear the input.
                if (target.dataset.action === 'clear') {
                    $lookupField.value = '';
                    clearMessage();
                    // Dispatch a couple handy events.
                    $lookupField.dispatchEvent(new Event('change'));
                    $lookupField.dispatchEvent(new Event('blur'));
                    dispatchEvent($form, 'agend.contact-lookup.contact-clear', {formData, data, $lookupField});
                    // ...Then enable buttons again.
                    $actionButtons.forEach($button => $button.removeAttribute('disabled'));

                    emptyFormFields($form, $lookupField, formData);
                    setAllFieldSensitivityStates(formData, false);
                    maybeUnlockFields();

                } else {
                    // First check the validity of the input.
                    if (!$lookupField.checkValidity()) {
                        $lookupField.reportValidity();

                        // ...Then enable buttons again.
                        $actionButtons.forEach($button => $button.removeAttribute('disabled'));
                        return;
                    }

                    dispatchEvent($form, 'agend.contact-lookup.lookup-initiated', {formData, data, $lookupField});
                    maybeLockFields();

                    // ...or lookup based on input.
                    performLookup().finally(() => {
                        // ...Then enable buttons again.
                        $actionButtons.forEach($button => $button.removeAttribute('disabled'));
                        dispatchEvent($form, 'agend.contact-lookup.lookup-completed', {formData, data, $lookupField});
                    });
                }
            },
            200
        );

        // And when someone presses [enter] in the input, lookup.
        $lookupField.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter') {
                return;
            }

            event.stopImmediatePropagation();
            event.preventDefault();

            // Dispatch a click on the lookup button.
            $actionContainer.querySelector('[data-action="lookup"]').click();

            return false;
        });

        appendElement($actionContainer);
    } else {
        // ...otherwise watch for input events.
        throttledEvent($lookupField, 'input', () => {
            maybeLockFields();

            // ...or lookup based on input.
            performLookup().finally(() => {
                // ...Then enable buttons again.
                $actionButtons.forEach($button => $button.removeAttribute('disabled'));
            });
        });
    }

    // Add in the message container.
    if ($lookupFieldContainer) {
        $lookupFieldContainer.append($messageContainer);
    } else {
        $lookupField.after($messageContainer);
    }

    // Track the "previous state" of whether the lookup field was visible or not..
    let lookupFieldVisible = isVisible($lookupField);

    // If the lookup has a value, lock fields and show the pre-set message.
    const setInitialFormState = () => {
        maybeSetConditionalFieldsState(formData);

        // We do a dodgy timeout, as Gravity Forms and/or GF Perks and/or Agend field visibility management
        // is in play prior to this code, resulting in fields potentially being hidden.
        window.setTimeout(() => {
            const visible = isVisible($lookupField);

            if (visible) {
                if ($lookupField.value.trim().length) {
                    maybeLockFields();
                    const data = {contact: {}};
                    data.contact[lookupField.upbeat_property] = $lookupField.value;
                    setMessage(formData.phrases.contact_currently_set, data);
                    dispatchEvent($form, 'agend.contact-lookup.contact-pre-populated', {formData, data, $lookupField});
                } else {
                    // Clear messages if the lookup field is empty, as a previous value may have been cleared by
                    // the form visibility behaviour e.g. attendee registration type changes.
                    clearMessage();
                    maybeSetConditionalFieldsState(formData);
                    maybeUnlockFields();
                }
            }

            lookupFieldVisible = visible;
        }, 100);

    };

    // Set the form state now that we've set things up.
    setInitialFormState();

    // Set up a mutation observer to watch for when the lookup field becomes visible or hidden.
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            // Not the lookup field, we don't care about this mutation.
            if (mutation.target !== $lookupField) {
                return;
            }

            const visible = isVisible($lookupField);

            // If the lookup field is now visible, set the initial form state.
            if (visible && !lookupFieldVisible) {
                setInitialFormState();
            }

            // Update the visibility state.
            lookupFieldVisible = visible;
        });
    });

    // Watch the whole lookup container.
    observer.observe($lookupFieldContainer, {attributes: true, subtree: true});

    // Regrettably we need a little bit of a timeout here, as GF may not even have presented
    // a form yet, resulting in always having a value of 0.
    window.setTimeout(resolveSizes, 100);

    // ..and also for resize, as heights and position may adjust.
    throttledEvent(window, 'resize', resolveSizes, 100);

    // ...and finally we observe visual intersections, which allows us to handle state when the
    // field is hidden or revealed by other behaviours in the page.
    try {
        const options = {
            root: $form,
            rootMargin: '0px',
            // Use a low enough threshold to avoid false positives.
            threshold: 0.5
        }

        const observer = new IntersectionObserver((entries, observer) => {
            // Are any of the entries (our element) actually visible now?
            if (entries.filter(entry => entry.boundingClientRect.width > 0).length) {
                // Yes, so let's update sizing.
                resolveSizes();
            }
        }, options);
        observer.observe($lookupFieldContainer || $lookupField);
    } catch (e) {
        console.warn('Could not observe field for layout changes.', e);
    }

    dispatchEvent($form, 'agend.contact-lookup.initialised', {$form, formData, $lookupField, $lookupFieldContainer});
}

/**
 * Find a field element by its upbeat property.
 *
 * @param upbeatProperty The upbeat contact property.
 * @param fields
 * @param firstOrLast
 * @returns {*|null}
 */
function findLookUpFieldElementByUpbeatProperty(upbeatProperty, fields, firstOrLast = 'first') {
    const field = window.resolveLookupField(upbeatProperty, fields, firstOrLast);
    if (!field) {
        return null;
    }

    return document.querySelector(field['field_selector']);
}

// Make this function accessible to client plugins.
window.findLookUpFieldElementByUpbeatProperty = findLookUpFieldElementByUpbeatProperty;

/**
 * Creates a selector for a field, based on its defined attributes, excluding style and aria
 * attributes. Do note that in a broader context, the result could match multiple fields! eg
 * passing in a <div> with no attributes will match any div if used.
 * @param $element
 * @return {string}
 */
function createSelectorForElement($element) {
    const attributeNames = $element.getAttributeNames().filter(name => !/style|aria/.test(name));
    return $element.tagName + attributeNames.map(name => {
        const v = $element.getAttribute(name);
        return `[${name}${!v ? '' : `="${v}"`}]`
    }).join('');
}

/**
 * Detect the height of the field, accounting for the parent being hidden.
 *
 * @param {HTMLElement|null} $container The container of the field.
 * @param {HTMLElement} $field The field to check.
 * @return {number} The resultant height of the field.
 */
function resolveFieldHeight($container, $field) {

    if (!$field) {
        return 0;
    }

    const $elementToClone = $container || $field;

    // Make a clone of the element.
    const $clone = $elementToClone.cloneNode(true);

    // Make invisible and remove from flow, and prevent interactions.
    $clone.style.setProperty('opacity', '0.2');
    $clone.style.setProperty('pointer-events', 'none');

    // Make it visible if it's hidden.
    if ($clone.style.getPropertyValue('display') === 'none') {
        $clone.style.removeProperty('display');
    }

    $elementToClone.after($clone);

    // Determine which element to use for the height detection.
    const $heightElement = $container
        ? $clone.querySelector(createSelectorForElement($field))
        : $clone;

    // Detect height before removal.
    const height = $heightElement?.getBoundingClientRect()?.height || 0;

    $clone.remove();

    return height;
}

/**
 * Defines and/or modifies the push method on the collection of form data, so
 * that behaviour can apply to any async loaded forms with our JS snippet.
 */
function prepareBoundFormsList() {
    window.agendUpbeatContactLookupForms = window.agendUpbeatContactLookupForms || [];

    const originalPushMethod = window.agendUpbeatContactLookupForms.push;

    window.agendUpbeatContactLookupForms.push = function (...newItems) {
        // Init the forms...
        newItems.forEach(formData => prepareFormForLookup(formData));
        // ...and then append to array as per usual.
        originalPushMethod.call(window.agendUpbeatContactLookupForms, ...newItems);
    }
}

document.addEventListener('DOMContentLoaded', event => {

    // Prepare to catch future forms.
    prepareBoundFormsList();

    // Initialise existing forms.
    window.agendUpbeatContactLookupForms.forEach(formData => prepareFormForLookup(formData));
});


window.addEventListener('tribe_et_after_form_prefills', event => {
    // Attempt again to initialise forms. Tribe may not have been ready when we did it with document ready.
    window.agendUpbeatContactLookupForms.forEach(formData => prepareFormForLookup(formData));
});

/**
 * Maybe unlock fields if the lock_property_fields_is_empty setting is false and the field is empty.
 *
 * This setting can be disabled by adding this filter to your client plugin:
 * add_filter( 'agend_contact_lookup_form_binding_lock_property_fields_if_empty', '__return_false' );
 *
 * @param event
 */
function eventMaybeUnlockFieldIfPopulatedValueIsEmpty(event) {
    if (!event || !event.detail || !event.detail.formData) {
        return;
    }

    const { formData } = event.detail;

    // If the setting is to lock fields when empty (default), do nothing.
    if (formData.lock_property_fields_if_empty) {
        return;
    }

    // Loop through each field and unlock it if it's empty.
    formData.fields.forEach(field => {
        if (!field._$field.value.trim()) {
            unlockField(field._$field, formData);
        }
    });
}

const contactPopulatedEvents = [
    'agend.contact-lookup.contact-found',
    'agend.contact-lookup.contact-pre-populated'
];

contactPopulatedEvents.forEach(eventType => {
    window.addEventListener(eventType, eventMaybeUnlockFieldIfPopulatedValueIsEmpty);
});


/**
 * Handle title and title other field state if these fields are defined.
 */
function maybeSetConditionalFieldsState(formData) {
    const $titleField = findLookUpFieldElementByUpbeatProperty(title_property, formData?.fields);
    const $titleOtherField = findLookUpFieldElementByUpbeatProperty(title_other_property, formData?.fields );

    if (!$titleField || !$titleOtherField) {
        return;
    }

    // Maybe hide Salutation other.
    if ($titleField.value !== 'OTHER') {
        $titleOtherField.closest('.gfield,.tribe-tickets__form-field').style.display = 'none';
    }

    $titleField.addEventListener('change', function () {
        if ($titleField.value === 'OTHER') {
            $titleOtherField.closest('.gfield,.tribe-tickets__form-field').style.display = isTribe ? 'flex' : 'block';
            $titleOtherField.removeAttribute('disabled');
        } else {
            $titleOtherField.closest('.gfield,.tribe-tickets__form-field').style.display = 'none';
        }
    });
}
